import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest, postRequest, patchRequest, deleteRequest } from "services/api";

import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import { buildQueryString } from "utils/queryHelpers";

const SalaryContext = createContext();

const toDateKey = (date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, "0"),
  String(date.getDate()).padStart(2, "0"),
].join("-");

const getPayrollPeriod = (month, year) => {
  if (!month || !year) return { startDate: "", endDate: "" };
  const start = new Date(Number(year), Number(month) - 2, 26);
  const end = new Date(Number(year), Number(month) - 1, 25);
  return {
    startDate: toDateKey(start),
    endDate: toDateKey(end),
    dateSalary: toDateKey(new Date(Number(year), Number(month) - 1, 1)),
  };
};

export const SalaryProvider = ({ children }) => {
  const [salaries, setSalaries] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    perPage: 50,
    include: "insurances,facilities,deductions,workBonuses,others,user",
    orderBy: "date_salary",
    sort: "desc",
    all: 0,
    filterMonth: "",
    filterYear: "",
    filterUserId: "",
    filterDepartmentId: "",
  });

  const payrollPeriod = getPayrollPeriod(filterOptions.filterMonth, filterOptions.filterYear);
  const dateSalaryFilter = payrollPeriod.dateSalary;

  const buildSalaryQueryString = useCallback((dateFilter = dateSalaryFilter, overrides = {}) => buildQueryString({
    search: filterOptions.search || null,
    page: filterOptions.currentPage,
    limit: filterOptions.perPage,
    include: filterOptions.include,
    orderBy: filterOptions.orderBy,
    sort: filterOptions.sort,
    all: filterOptions.all,
    'filter[id]': null,
    'filter[user_id]': filterOptions.filterUserId || null,
    'filter[department_id]': filterOptions.filterDepartmentId || null,
    'filter[date_salary]': dateFilter || null,
    ...overrides,
  }), [dateSalaryFilter, filterOptions]);

  const fetchSalaries = useCallback(async () => {
    setSalaries((prev) => ({ ...prev, loading: true }));

    try {
      const response = await getRequest(`/ahr/salaries${buildSalaryQueryString(dateSalaryFilter)}`);

      if (response.success) {
        setSalaries({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });
      } else {
        throw new Error("Failed to fetch salary data");
      }
    } catch (error) {
      setSalaries((prev) => ({
        ...prev,
        error: error.message,
        loading: false,
      }));
    }
  }, [buildSalaryQueryString, dateSalaryFilter]);

  useEffect(() => {
    fetchSalaries();
  }, [fetchSalaries]);

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      const searchValue = e.target.value.trim();
      if (searchValue !== filterOptions.search) {
        setFilterOptions((prev) => ({
          ...prev,
          search: searchValue,
          currentPage: 1,
        }));
      }
    }
  };

  const handlePageChange = (page) => {
    setFilterOptions((prev) => ({ ...prev, currentPage: page }));
  };

  const handlePerPageChange = (perPage) => {
    setFilterOptions((prev) => ({ ...prev, perPage: perPage, currentPage: 1 }));
  };

  const handleMonthFilterChange = (month) => {
    setFilterOptions((prev) => ({
      ...prev,
      filterMonth: month,
      currentPage: 1,
    }));
  };

  const handleYearFilterChange = (year) => {
    setFilterOptions((prev) => ({
      ...prev,
      filterYear: year,
      currentPage: 1,
    }));
  };

  const handleUserFilterChange = (userId) => {
    setFilterOptions((prev) => ({
      ...prev,
      filterUserId: userId,
      currentPage: 1,
    }));
  };

  const handleDepartmentFilterChange = (departmentId) => {
    setFilterOptions((prev) => ({
      ...prev,
      filterDepartmentId: departmentId,
      filterUserId: "",
      currentPage: 1,
    }));
  };

  const Pagination = usePaginationWithComponent({
    totalPages: salaries.pagination?.last_page || 1,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  const generateSalary = useCallback(async (data) => {
    try {
      const response = await postRequest(`/ahr/generate-salary`, data);
      return response;
    } catch (error) {
      throw error;
    }
  }, []);

  return (
    <SalaryContext.Provider
      value={{
        salaries,
        filterOptions,
        handleSearch,
        handlePerPageChange,
        handleMonthFilterChange,
        handleYearFilterChange,
        handleUserFilterChange,
        handleDepartmentFilterChange,
        payrollPeriod,
        refetch: () => fetchSalaries(),
        generateSalary,
        fetchAllSalaries: async () => {
          try {
            const response = await getRequest(`/ahr/salaries${buildSalaryQueryString(dateSalaryFilter, {
              page: null,
              limit: null,
              all: 1,
            })}`);
            return response;
          } catch (error) {
            throw error;
          }
        },
        sendEmailSalary: async (payload) => {
          // 🛠️ MOCK: Eksekusi di FE karena API BE belum ready
          return new Promise((resolve) => {
            console.log("Simulasi Pengiriman Email (FE Execution):", payload);
            
            // Simulasi loading 1.5 detik
            setTimeout(() => {
              resolve({
                success: true,
                message: payload.is_batch 
                  ? "Berhasil mensimulasikan pengiriman email massal!" 
                  : "Berhasil mensimulasikan pengiriman email satuan!"
              });
            }, 1500);
          });
        },
        updateSalary: async (id, data) => {
          try {
            const response = await patchRequest(`/ahr/salaries/${id}`, data);
            return response;
          } catch (error) {
            throw error;
          }
        },
        deleteSalary: async (id) => {
          try {
            const response = await deleteRequest(`/ahr/salaries/${id}`);
            return response;
          } catch (error) {
            throw error;
          }
        },

        Pagination,
      }}
    >
      {children}
    </SalaryContext.Provider>
  );
};

export const useSalary = () => {
  const context = useContext(SalaryContext);
  if (!context) {
    throw new Error("useSalary must be used within SalaryProvider");
  }
  return context;
};
